import {
  addresses,
  customerStatusEnum,
  customerTypeEnum,
  properties,
  propertyTypeEnum,
  subscriptionStatusEnum,
} from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import { withAttachments } from './attachment'

export type PropertyType = (typeof propertyTypeEnum.enumValues)[number]

export const selectPropertySchema = createSelectSchema(properties)

export const insertPropertySchema = createInsertSchema(properties).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  addressId: true,
})

export const updatePropertySchema = insertPropertySchema.partial()

export const selectAddressSchema = createSelectSchema(addresses)

export const insertAddressSchema = createInsertSchema(addresses).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const updateAddressSchema = insertAddressSchema.partial()

// Extended schemas for property with address
export const propertyWithAddressSchema = selectPropertySchema.extend({
  address: selectAddressSchema,
})

export const selectPropertyWithAddressSchema = withAttachments(
  selectPropertySchema.extend({
    address: z
      .object({
        id: z.number(),
        street: z.string(),
        city: z.string(),
        postalCode: z.string().nullable(),
        country: z.string().nullable(),
        latitude: z.string().nullable(),
        longitude: z.string().nullable(),
        streetViewCameraLat: z.string().nullable(),
        streetViewCameraLng: z.string().nullable(),
        streetViewHeading: z.string().nullable(),
      })
      .nullable(),
    customer: z
      .object({
        id: z.number(),
        name: z.string(),
        type: z.enum(customerTypeEnum.enumValues),
        status: z.enum(customerStatusEnum.enumValues),
      })
      .nullable()
      .optional(),
    subscriptions: z
      .array(
        z.object({
          id: z.number(),
          status: z.enum(subscriptionStatusEnum.enumValues),
          displayName: z.string().nullable().optional(),
          additionalTerms: z.string().nullable(),
          basePriceMonthly: z.string().nullable().optional(),
          effectivePriceMonthly: z.string().nullable().optional(),
          subscriptionPackageVersions: z
            .array(
              z.object({
                id: z.number(),
                quantity: z.number(),
                sortOrder: z.number(),
                servicePackageVersion: z.object({
                  id: z.number(),
                  versionNumber: z.number(),
                  priceMonthly: z.string(),
                }),
              }),
            )
            .nullable()
            .optional(),
        }),
      )
      .nullable()
      .optional(),
  }),
)

export const insertPropertyWithAddressSchema = insertPropertySchema.extend({
  address: insertAddressSchema,
})

export const updatePropertyWithAddressSchema = updatePropertySchema.extend({
  address: updateAddressSchema.partial(),
})

// Type exports
export type Property = z.infer<typeof selectPropertySchema>
export type CreateProperty = z.infer<typeof insertPropertySchema>
export type UpdateProperty = z.infer<typeof updatePropertySchema>
export type Address = z.infer<typeof selectAddressSchema>
export type CreateAddress = z.infer<typeof insertAddressSchema>
export type UpdateAddress = z.infer<typeof updateAddressSchema>
export type PropertyWithAddress = z.infer<typeof propertyWithAddressSchema>
export type PropertyWithRelations = z.infer<
  typeof selectPropertyWithAddressSchema
>
export type CreatePropertyWithAddress = z.infer<
  typeof insertPropertyWithAddressSchema
>
export type UpdatePropertyWithAddress = z.infer<
  typeof updatePropertyWithAddressSchema
>

// Helper types for nested data
export type PropertySubscription = NonNullable<
  PropertyWithRelations['subscriptions']
>[number]
export type PropertyAddress = NonNullable<PropertyWithRelations['address']>
